import React, { useState, useImperativeHandle, forwardRef } from "react";
import "./ApplicationForm.scss";

const ApplicationForm = forwardRef(({ job, recordedVideo }, ref) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
  });

  const [resume, setResume] = useState(null);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, resume: "File size must be less than 5MB" }));
        return;
      }
      // Validate file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({ ...prev, resume: "Only PDF and DOC files are allowed" }));
        return;
      }
      setResume(file);
      setErrors(prev => ({ ...prev, resume: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) newErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }
    if (!formData.phone.trim()) newErrors.phone = "Phone number is required";
    if (!formData.address.trim()) newErrors.address = "Address is required";
    if (!resume) newErrors.resume = "Resume is required";

    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;
    return isValid;
  };

  const getFormData = () => {
    const submitData = new FormData();
    
    // Match backend expected field names
    submitData.append("positionId", job._id);
    submitData.append("firstName", formData.firstName);
    submitData.append("lastName", formData.lastName);
    submitData.append("email", formData.email);
    submitData.append("phone", formData.phone);
    submitData.append("address", formData.address);
    submitData.append("location", formData.address); // Also set location for compatibility
    
    // Important: field name must match middleware uploadFieldName
    submitData.append("resume", resume);

    return submitData;
  };

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    getFormData,
    validateForm,
    formData,
    resume,
    errors
  }));

  return (
    <form className="application-form" onSubmit={(e) => e.preventDefault()}>
      {errors.submit && (
        <div className="alert alert-danger">{errors.submit}</div>
      )}

      <div className="form-group">
        <label>First Name <span className="required">*</span></label>
        <input
          type="text"
          name="firstName"
          value={formData.firstName}
          onChange={handleChange}
          className={errors.firstName ? "error" : ""}
          placeholder="Enter your first name"
        />
        {errors.firstName && <span className="error-message">{errors.firstName}</span>}
      </div>

      <div className="form-group">
        <label>Last Name <span className="required">*</span></label>
        <input
          type="text"
          name="lastName"
          value={formData.lastName}
          onChange={handleChange}
          className={errors.lastName ? "error" : ""}
          placeholder="Enter your last name"
        />
        {errors.lastName && <span className="error-message">{errors.lastName}</span>}
      </div>

      <div className="form-group">
        <label>Email <span className="required">*</span></label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className={errors.email ? "error" : ""}
          placeholder="your.email@example.com"
        />
        {errors.email && <span className="error-message">{errors.email}</span>}
      </div>

      <div className="form-group">
        <label>Phone Number <span className="required">*</span></label>
        <input
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          className={errors.phone ? "error" : ""}
          placeholder="+1 (555) 123-4567"
        />
        {errors.phone && <span className="error-message">{errors.phone}</span>}
      </div>

      <div className="form-group">
        <label>Address <span className="required">*</span></label>
        <input
          type="text"
          name="address"
          value={formData.address}
          onChange={handleChange}
          className={errors.address ? "error" : ""}
          placeholder="City, Country"
        />
        {errors.address && <span className="error-message">{errors.address}</span>}
      </div>

      <div className="form-group">
        <label>Resume/CV <span className="required">*</span></label>
        <input
          type="file"
          accept=".pdf,.doc,.docx"
          onChange={handleFileChange}
          className={errors.resume ? "error" : ""}
        />
        {resume && <span className="file-name">Selected: {resume.name}</span>}
        {errors.resume && <span className="error-message">{errors.resume}</span>}
        <small>Accepted formats: PDF, DOC, DOCX (Max 5MB)</small>
      </div>

      <div className="form-note">
        <p><small>* Required fields</small></p>
        <p><small>Your information will be kept confidential in accordance with our Privacy Policy.</small></p>
      </div>
    </form>
  );
});

ApplicationForm.displayName = 'ApplicationForm';

export default ApplicationForm;
